/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * sosCountdownGuard — the pure decision behind the safety-critical "Cancel
 * SOS" fix (#1 web QA). Extracted so the cancel/confirm race is unit-
 * testable without Firebase or React.
 *
 * The bug: clicking "Cancel SOS" during the countdown could still let the
 * alert fire (confirm → SMS). clearInterval() alone is not enough — an
 * interval callback that has ALREADY fired (queued onto the task queue in
 * the same tick as the cancel click) cannot be un-queued, so the confirm
 * it calls runs anyway.
 *
 * The fix is an abort latch set synchronously the instant cancel is
 * accepted, and checked synchronously at the top of confirm. This module
 * is that check, isolated.
 */

export interface SosConfirmGate {
  /** True once Cancel was accepted for the current countdown. */
  aborted: boolean;
  /** True if there is a signed-in user (no user → nothing to write). */
  hasUser: boolean;
}

/**
 * Decide whether a confirm attempt may proceed to write `status:"active"`
 * and send the emergency SMS. Returns false (suppress) when the user
 * cancelled or there's no signed-in user.
 *
 * This is intentionally a pure function of the latch + user presence — NOT
 * of the remaining countdown time — because the whole point is to suppress
 * a confirm that was already in flight when cancel landed.
 */
export function shouldProceedWithConfirm(gate: SosConfirmGate): boolean {
  if (!gate.hasUser) return false;
  if (gate.aborted) return false;
  return true;
}
